/**
 * InvitationRowActions Component
 *
 * Action buttons for a pending invitation row in UsersTable.
 * - `variant="dropdown"` renders a ⋮ dropdown menu (mobile).
 * - `variant="inline"` renders icon buttons with tooltips (desktop).
 */
import { Button, Dropdown, Icon, Tooltip } from '@insio/ui-toolkit';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import type { InvitationData, UserData } from '../types';

interface InvitationRowActionsProps {
  invitation: InvitationData;
  variant: 'dropdown' | 'inline';
  onEdit: (user: UserData) => void;
  onResendInvitation: (invitation: InvitationData) => void;
  onCancelInvitation: (invitation: InvitationData) => void;
}

export function InvitationRowActions({
  invitation,
  variant,
  onEdit,
  onResendInvitation,
  onCancelInvitation
}: InvitationRowActionsProps) {
  const t = useFeatureTranslation('systemSettings.users');

  const handleEdit = () =>
    onEdit({
      id: invitation.id,
      email: invitation.email,
      firstName: '',
      lastName: '',
      displayName: invitation.email,
      roles: invitation.roles,
      status: 'PENDING',
      isSystemUser: false,
      identitySharedWithOtherAccounts: false,
      createdAt: invitation.createdAt,
      orgUnits: []
    });

  if (variant === 'dropdown') {
    return (
      <Dropdown
        triggerButton={
          <Button variant='ghost' size='sm' aria-label={t('fields.actions')}>
            <span className='text-lg leading-none tracking-widest select-none'>⋮</span>
          </Button>
        }
        align='bottom end'
        size='sm'
      >
        <Dropdown.Item icon='paper-plane' onClick={() => onResendInvitation(invitation)}>
          {t('actions.resendInvitation')}
        </Dropdown.Item>

        <Dropdown.Item
          icon='trash'
          onClick={() => onCancelInvitation(invitation)}
          className='text-error-text'
        >
          {t('actions.cancelInvitation')}
        </Dropdown.Item>
      </Dropdown>
    );
  }

  return (
    <div className='flex gap-xs justify-end'>
      <Tooltip label={t('actions.resendInvitation')} position='top'>
        <Button
          variant='ghost'
          size='sm'
          onClick={() => onResendInvitation(invitation)}
          aria-label={t('actions.resendInvitation')}
        >
          <Icon name='paper-plane' size='sm' className='text-primary-base' />
        </Button>
      </Tooltip>

      <Tooltip label={t('actions.edit')} position='top'>
        <Button
          variant='ghost'
          size='sm'
          onClick={handleEdit}
          aria-label={t('actions.edit')}
          disabled={true}
        >
          <Icon name='pen' size='sm' className='text-primary-base' />
        </Button>
      </Tooltip>

      <Tooltip label={t('actions.cancelInvitation')} position='top'>
        <Button
          variant='ghost'
          size='sm'
          onClick={() => onCancelInvitation(invitation)}
          aria-label={t('actions.cancelInvitation')}
        >
          <Icon name='trash' size='sm' className='text-error-text' />
        </Button>
      </Tooltip>
    </div>
  );
}
